import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp } from "lucide-react";
import { API } from "@/config";
import { Card } from "@/components/admin/ui";

/**
 * 30-day cash-flow chart backed by GET /admin/payouts/cashflow.
 *
 * Stacks "Settled" (already paid to bank) + "Scheduled" (still pending in
 * Stripe balance) per day so the operator sees at a glance how the Enchanted
 * Peace-of-Mind + Standard split-payout policy smooths their deposits.
 */
const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const shortDate = (iso) => {
  if (!iso) return "";
  const [_, m, d] = iso.split("-");
  return `${m}/${d}`;
};

const CashflowTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const scheduled = payload.find((p) => p.dataKey === "scheduled")?.value ?? 0;
  const settled = payload.find((p) => p.dataKey === "settled")?.value ?? 0;
  const total = scheduled + settled;
  return (
    <div className="rounded-lg border border-[#FFD166]/40 bg-[#0A2438]/95 backdrop-blur px-3 py-2 text-xs shadow-lg">
      <div className="text-[#F6F1E4] font-medium">{label}</div>
      {settled > 0 && <div className="text-emerald-300">Settled · ${settled.toFixed(2)}</div>}
      {scheduled > 0 && <div className="text-[#FFD166]">Scheduled · ${scheduled.toFixed(2)}</div>}
      {total > 0 && <div className="text-[#B9C8D5] pt-1 mt-1 border-t border-[#FFD166]/15">Total · ${total.toFixed(2)}</div>}
    </div>
  );
};

export const CashflowChart = ({ days = 30 }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let ok = true;
    axios.get(`${API}/admin/payouts/cashflow`, { params: { days } })
      .then((r) => { if (ok) setData(r.data); })
      .catch(() => { if (ok) setData({ series: [], totals: { scheduled_cents: 0, settled_cents: 0 } }); });
    return () => { ok = false; };
  }, [days]);

  const rows = useMemo(() => (data?.series || []).map((b) => ({
    date: shortDate(b.date),
    scheduled: (b.scheduled_cents || 0) / 100,
    settled: (b.settled_cents || 0) / 100,
  })), [data]);

  const isEmpty = !rows.some((r) => r.scheduled > 0 || r.settled > 0);

  return (
    <Card>
      <div className="p-5" data-testid="cashflow-chart">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Cash flow · next {days} days</h2>
          </div>
          <div className="flex gap-4 text-xs" data-testid="cashflow-totals">
            <div>
              <span className="uppercase tracking-wider text-[#B9C8D5]">Scheduled</span>{" "}
              <span className="text-[#FFD166] font-medium">{money(data?.totals?.scheduled_cents || 0)}</span>
            </div>
            <div>
              <span className="uppercase tracking-wider text-[#B9C8D5]">Settled</span>{" "}
              <span className="text-emerald-300 font-medium">{money(data?.totals?.settled_cents || 0)}</span>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[#B9C8D5] mt-1 mb-4">
          How much your nightly sweep will move each day, per Enchanted Peace of Mind + Standard split-payout policy.
        </p>
        <div className="h-64" data-testid="cashflow-canvas">
          {!data ? (
            <div className="h-full flex items-center justify-center text-[#B9C8D5] text-sm">Loading…</div>
          ) : isEmpty ? (
            <div className="h-full flex flex-col items-center justify-center text-[#B9C8D5] text-sm text-center gap-1" data-testid="cashflow-empty">
              <TrendingUp className="w-8 h-8 opacity-40" />
              <div>No scheduled payouts in the next {days} days yet.</div>
              <div className="text-[11px] opacity-70">Bookings paid today will show up on tomorrow's Care-fee sweep.</div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,209,102,0.08)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#B9C8D5", fontSize: 10 }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: "rgba(255,209,102,0.2)" }} />
                <YAxis tick={{ fill: "#B9C8D5", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "rgba(255,209,102,0.2)" }} tickFormatter={(v) => `$${v}`} />
                <Tooltip content={<CashflowTooltip />} cursor={{ fill: "rgba(255,209,102,0.08)" }} />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: "#B9C8D5", paddingTop: 4 }}
                  iconType="circle"
                  iconSize={8}
                  formatter={(v) => v === "settled" ? "Settled (paid to bank)" : "Scheduled (still in Stripe)"}
                />
                <Bar dataKey="settled" stackId="cash" fill="#34D399" radius={[0, 0, 0, 0]} />
                <Bar dataKey="scheduled" stackId="cash" fill="#FFD166" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Card>
  );
};

export default CashflowChart;
